/* Reception preferences never select a Shopify market, country or currency. */
(() => {
  const fr = document.documentElement.lang.toLowerCase().startsWith('fr');
  // Shopify's active country is independent of the cart's reception mode.
  const isLocal = () => document.documentElement.dataset?.shopifyCountry === 'CM';
  const normalize = value => ['EU', 'CM'].includes(String(value || '').trim().toUpperCase()) ? String(value).trim().toUpperCase() : '';
  let memory = '';
  let cartState = { mode: '', valid: false };
  let cartItems = [];
  const preference = () => {
    let cookie = '', session = '';
    try { cookie = normalize(decodeURIComponent(document.cookie.split(';').map(s => s.trim()).find(s => s.startsWith('delivery_zone='))?.slice(14) || '')); } catch { /* denied or malformed cookie */ }
    try { session = normalize(sessionStorage.getItem('delivery_zone')); } catch { /* denied storage */ }
    return memory || cookie || session;
  };
  const zone = () => preference() || (cartState.valid && cartState.mode) || 'EU';
  const notify = () => document.dispatchEvent(new CustomEvent('diaspora:delivery-zone-selected', { detail: { zone: zone() } }));
  const store = value => {
    memory = normalize(value);
    try { document.cookie = `delivery_zone=${memory}; max-age=604800; path=/; SameSite=Lax`; } catch { /* in-memory fallback */ }
    try { sessionStorage.setItem('delivery_zone', memory); } catch { /* in-memory fallback */ }
    notify();
  };
  const cmKeys = ['Reception method', 'Beneficiary full name', 'Beneficiary phone', 'Beneficiary city', 'Beneficiary district', 'Pickup person', 'Delivery details', 'Pickup notes', 'Message for beneficiary'];
  const classify = cart => {
    if (!cart || !Array.isArray(cart.items)) throw new Error('Invalid cart response');
    const modes = new Set();
    for (const line of cart.items) {
      const p = line.properties || {};
      const has = key => String(p[key] || '').trim().length > 0;
      const raw = String(p['_Delivery Zone'] || '').trim();
      const mode = normalize(raw);
      if ((raw && !mode) || (mode !== 'CM' && cmKeys.some(has))) return { valid: false, mode: '' };
      if (mode === 'CM') {
        if (!['Local delivery', 'Pickup'].includes(p['Reception method']) || !['Beneficiary full name', 'Beneficiary phone', 'Beneficiary city'].every(has) || (p['Reception method'] === 'Local delivery' && !has('Beneficiary district'))) return { valid: false, mode: '' };
      }
      modes.add(mode || 'EU');
    }
    return { valid: modes.size <= 1, mode: modes.size === 1 ? [...modes][0] : '', hasCM: modes.has('CM') };
  };
  const readCart = async () => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { cache: 'no-store', signal: controller.signal });
      if (!response.ok) throw new Error('Cart unavailable');
      const cart = await response.json();
      cartState = classify(cart);
      cartItems = cart.items;
      updateUI();
      return cartState;
    } finally { clearTimeout(timer); }
  };
  const invalidMessage = fr ? "Votre panier contient des modes de réception mélangés ou des informations Cameroun incomplètes ou incohérentes. Vérifiez et retirez manuellement les lignes concernées avant de continuer. Votre panier n'a pas été modifié." : 'Your cart contains mixed reception modes or incomplete or inconsistent Cameroon information. Please review and manually remove the affected items before continuing. Your cart has not been changed.';
  const networkMessage = fr ? "Impossible de vérifier votre panier. Réessayez. Votre panier n'a pas été modifié." : 'Unable to verify your cart. Please try again. Your cart has not been changed.';
  const localMessage = fr ? 'Les commandes locales au Cameroun sont finalisées directement avec VitalSpirit Health. Contactez-nous sur WhatsApp pour confirmer la disponibilité, la livraison ou le retrait et le paiement par Orange Money, MTN Mobile Money ou espèces.' : 'Local Cameroon orders are finalized directly with VitalSpirit Health. Contact us on WhatsApp to confirm availability, delivery or pickup, and payment by Orange Money, MTN Mobile Money, or cash.';
  const cartMessage = items => {
    const pending = fr ? 'à confirmer' : 'to confirm';
    const lines = items.map(item => {
      const p = item.properties || {};
      const method = p['Reception method'];
      const reception = method === 'Local delivery' ? (fr ? 'livraison locale' : 'local delivery') : method === 'Pickup' ? (fr ? 'retrait' : 'pickup') : pending;
      const variant = item.variant_title && item.variant_title !== 'Default Title' ? ` / ${item.variant_title}` : '';
      return `${item.product_title || item.title || pending}${variant} × ${item.quantity}. ${fr ? 'Réception' : 'Reception'}: ${reception}. ${fr ? 'Ville' : 'City'}: ${String(p['Beneficiary city'] || '').trim() || pending}.`;
    });
    const intro = fr ? 'Bonjour VitalSpirit Health, je souhaite passer une commande locale au Cameroun.' : 'Hello VitalSpirit Health, I would like to place a local Cameroon order.';
    const end = fr ? 'Merci de confirmer la disponibilité, le total, la livraison/retrait et les modalités de paiement Orange Money, MTN Mobile Money ou espèces.' : 'Please confirm availability, the total, delivery/pickup and payment arrangements via Orange Money, MTN Mobile Money or cash.';
    return [intro, ...lines, end].join('\n');
  };
  const showMessage = (host, message) => {
    let box = host.querySelector('[data-reception-error]');
    if (!box) {
      box = document.createElement('div'); box.dataset.receptionError = ''; box.setAttribute('role', 'alert'); box.tabIndex = -1;
      host.append(box);
    }
    box.replaceChildren();
    const text = document.createElement('p'); text.textContent = message; box.append(text);
    const keep = document.createElement('button'); keep.type = 'button'; keep.className = 'button button--secondary'; keep.textContent = fr ? 'Conserver ce mode' : 'Keep current mode';
    keep.onclick = () => { box.remove(); document.querySelector('diaspora-selector')?.close(); };
    const link = document.createElement('a'); link.href = window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`; link.className = 'link'; link.textContent = fr ? 'Voir le panier' : 'View cart';
    box.append(keep, document.createTextNode(' '), link); box.focus();
  };
  const guard = async (requested, host, checkout = false) => {
    host.querySelector('[data-reception-error]')?.remove();
    if (checkout && isLocal()) { showMessage(host, localMessage); return false; }
    try {
      const state = await readCart();
      if (!state.valid) { showMessage(host, invalidMessage); return false; }
      if (!checkout && state.mode && state.mode !== normalize(requested)) {
        const mode = state.mode === 'CM' ? (fr ? 'la réception au Cameroun' : 'Cameroon reception') : (fr ? 'la livraison Standard' : 'Standard delivery');
        showMessage(host, fr ? `Votre panier contient déjà des articles pour ${mode}. Veuillez terminer votre commande ou vider votre panier avant de changer de mode de réception. Votre panier n'a pas été modifié.` : `Your cart already contains items for ${mode}. Please complete your order or empty your cart before changing reception mode. Your cart has not been changed.`);
        return false;
      }
      return true;
    } catch { cartState = { valid: false, mode: '' }; updateUI(); showMessage(host, networkMessage); return false; }
  };
  function updateUI() {
    document.querySelectorAll('[data-dynamic-checkout]').forEach(el => { el.hidden = isLocal() || zone() === 'CM' || !cartState.valid || !!cartState.mode; });
    document.querySelectorAll('[data-cm-only]').forEach(el => { el.hidden = !isLocal() && zone() !== 'CM'; });
    document.querySelectorAll('.cart__dynamic-checkout-buttons').forEach(el => { el.hidden = isLocal() || !cartState.valid || cartState.mode === 'CM'; });
    document.querySelectorAll('[name="checkout"], a[href$="/checkout"]').forEach(el => { el.hidden = isLocal(); });
  }
  window.VitalReception = { normalize, preference, zone, store, classify, readCart, guard, isLocal, cartMessage };
  document.addEventListener('diaspora:delivery-zone-selected', updateUI);
  // Delegate to survive drawer and section replacements. Re-read before normal checkout.
  const checkout = async (event, control, form) => {
    event.preventDefault(); event.stopImmediatePropagation();
    if (form?.dataset.receptionChecking) return;
    if (form) form.dataset.receptionChecking = 'true';
    const host = control?.closest('.cart__blocks, .drawer__footer') || form || document.querySelector('main');
    try {
      if (await guard('', host, true)) {
        if (form) {
          const field = document.createElement('input'); field.type = 'hidden'; field.name = 'checkout'; field.value = 'Checkout'; form.append(field);
          HTMLFormElement.prototype.submit.call(form);
        } else window.location.assign(`${window.Shopify?.routes?.root || '/'}checkout`);
      }
    } finally { if (form) delete form.dataset.receptionChecking; }
  };
  document.addEventListener('click', event => {
    const control = event.target.closest('[name="checkout"], a[href$="/checkout"]');
    if (control) return checkout(event, control, control.form || control.closest('form'));
  }, true);
  document.addEventListener('submit', event => {
    if (event.submitter?.name === 'checkout') return checkout(event, event.submitter, event.target);
  }, true);
  const refresh = () => readCart().then(notify).catch(() => { cartState = { mode: '', valid: false }; updateUI(); });
  document.addEventListener('DOMContentLoaded', () => {
    refresh();
    if (typeof subscribe === 'function') subscribe(PUB_SUB_EVENTS.cartUpdate, refresh);
    const observer = new MutationObserver(records => { if (records.some(r => r.addedNodes.length)) updateUI(); });
    observer.observe(document.body, { childList: true, subtree: true });
  });
  window.addEventListener('pageshow', refresh);
  document.addEventListener('shopify:section:load', refresh);
  document.addEventListener('click', async event => {
    const link = event.target.closest('[data-cameroon-whatsapp]');
    if (!link) return;
    if (link.dataset.cartOrder !== undefined) {
      if (!isLocal()) return;
      event.preventDefault();
      if (link.dataset.loading) return;
      link.dataset.loading = 'true';
      const host = link.closest('[data-local-order]') || link.parentElement;
      try {
        await readCart();
        // Allowlisted fields only: never serialize the raw cart or its properties.
        const url = `https://wa.me/${link.dataset.number}?text=${encodeURIComponent(cartMessage(cartItems))}`;
        window.location.assign(url);
      } catch { showMessage(host, networkMessage); }
      finally { delete link.dataset.loading; }
      return;
    }
    const productForm = link.closest('product-form');
    const method = productForm?.querySelector('[data-cameroon-method]:checked')?.dataset.cameroonMethod;
    const reception = method === 'local' ? (fr ? 'livraison locale' : 'local delivery') : method === 'pickup' ? (fr ? 'retrait' : 'pickup') : (fr ? 'à confirmer' : 'to confirm');
    const city = productForm?.querySelector('[name="properties[Beneficiary city]"]')?.value.trim() || (fr ? 'à confirmer' : 'to confirm');
    const variantId = productForm?.querySelector('[name="id"]')?.value;
    let variant = '';
    try { variant = JSON.parse(link.dataset.variants || '[]').find(v => String(v.id) === variantId)?.title || ''; } catch { /* generic title */ }
    const title = [link.dataset.product || (fr ? 'à confirmer' : 'to confirm'), variant !== 'Default Title' ? variant : ''].filter(Boolean).join(' / ');
    const message = fr ? `Bonjour VitalSpirit Health, je souhaite passer une commande locale au Cameroun. Produit : ${title}. Réception : ${reception}. Ville : ${city}. Merci de confirmer la disponibilité, le total et les modalités de paiement.` : `Hello VitalSpirit Health, I would like to place a local Cameroon order. Product: ${title}. Reception: ${reception}. City: ${city}. Please confirm availability, the total and payment arrangements.`;
    link.href = `https://wa.me/${link.dataset.number}?text=${encodeURIComponent(message)}`;
  });
})();
